<template>
  <div v-if="isLoading">
    <div class="dots-loader">
    </div>
    <p class="absolute top-1/3 left-1/2 transform -translate-x-1/2">Loading, please wait ..</p>
  </div>
  <div v-else>
    <template v-if="authStore.maintenanceInfo.a === true">
      <UnderMaintenance/>
    </template>
    <template v-else>
      <component :is="layout"></component>
    </template>
  </div>
</template>
<script setup lang="ts">
import {useRoute, useRouter} from "vue-router";
import {ref, computed, watch} from 'vue';
import {useAuthStore} from "@/stores/auth";
import {useApiStore} from "@/stores/api";
import type {UserData} from "@/types/users/user";
import UnderMaintenance from "@/components/layout/UnderMaintenance.vue";
import {MaintenanceInfo} from "@/types/MaintenanceInfo";
import {Release, type ReleaseData} from "@/types/site_admin/releases";
import type {QueryFilter} from "@/types/object_classes/QueryFilter";
import {UserSetting} from "@/types/userSettings";
import {useIdleTimeout} from "@/helpers/useIdleTimeout";

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const isLoading = ref(true);
const api = useApiStore();

useIdleTimeout();

const layout = computed(() => {
  return route.meta.layout || "LayoutLoggedIn";
});

const routeName = computed(() => {
  return route.name;
});

const PUBLIC_UTILITY_ROUTES = ["unsubscribe"];
const onPublicUtilityRoute = computed(() =>
    PUBLIC_UTILITY_ROUTES.includes(routeName.value as string)
);

router.isReady().then(async () => {
  const onAuthHandoffRoute = route.name === 'callback';
  await authStore.initApp({skipRefresh: onAuthHandoffRoute || onPublicUtilityRoute.value});
  fetchData();
})

watch(() => authStore.token, (newToken, oldToken) => {
  if (!isLoading.value && newToken && !oldToken) {
    isLoading.value = true;
    fetchData();
  }
})

const fetchData = () => {
  const loadPromises: Promise<any>[] = [];
  loadPromises.push(api.getCustomAction('maintenances', null, 'isMaintenanceActive'));
  const shouldFetchUser = !onPublicUtilityRoute.value && (!!authStore.token || routeName.value !== "callback");
  if (shouldFetchUser) {
    loadPromises.push(api.axiosAuth.get("/user"));
  }

  Promise.allSettled(loadPromises)
      .then(async (result) => {
        if (result[0].status !== "fulfilled") {
          console.log("failed reading maintenance data");
          // return;
        }
        authStore.maintenanceInfo = new MaintenanceInfo(result[0].value);

        if (!shouldFetchUser) {
          return;
        }

        if (result[1].status !== "fulfilled") {
            authStore.isLoggedIn = false
            if (!["login", "callback", "unsubscribe"].includes(routeName.value as string)) {
              await router.push({name: "login"});
            }
        }

        if (result[1].status === "fulfilled") {
          authStore.isLoggedIn = true;
          authStore.setUser(result[1].value.data as UserData)

          const adminReleaseFilter: QueryFilter = {
            delimiter: null,
            field: "valid_entries",
            values: ['admin']
          }
          await api.getItems<Release, ReleaseData>(
              'releases',
              Release,
              undefined,
              {releases: ['admin_release_date']},
              [adminReleaseFilter],
              undefined,
              ['-admin_release_date', '-id'],
              undefined,
              {ignore_errors: true}
          )
              .then(async (releases) => {
                if(authStore.user.lastReleaseRead === null || (new Date(authStore.user.lastReleaseRead.data.release_date)).getTime() - releases[0].admin_release_date.getTime() < 0)
                {
                  const last_release_setting = new UserSetting();
                  last_release_setting.name = 'last_release_read';
                  last_release_setting.data = {
                    release_id: releases[0].id,
                    release_date: releases[0].admin_release_date
                  };

                  if(authStore.user.lastReleaseRead === null) {
                    api.createItem("user-settings", last_release_setting, UserSetting);
                  }
                  else {
                    last_release_setting.id = authStore.user.lastReleaseRead.id;
                    api.saveItem("user-settings", authStore.user.lastReleaseRead.id, last_release_setting, UserSetting);
                  }

                  await router.push({ name: 'news'})
                  return;
                }
              })
              .catch((error) => {
                console.log("failed reading releases data");
              })

          if (routeName.value === "login") {
            await router.push({name: "klg-users-listing"});
          }
        }
      })
      .finally(() => {
        isLoading.value = false;
      })
}

</script>

<style scoped>
body {
  overflow-x: hidden;
  background: #F8FAFB;
}
</style>
